import { useEffect, useState } from 'react';
import { FetchCallTypeFn, useFetchCallType } from '@rahtab/auth/lib/token-based-auth';
import { addLoads } from '../../rahtab-redux/slices/loadsSlice';
import { useAppDispatch, useSelector } from '../../rahtab-redux';
import { toNumber } from '@rahtab/system/lib/tools/string';
import { inventoryMerchandiseSelector, MerchandiseInventory } from 'rahtab-redux/slices/merchandiseInventorySlice';
import { ListScreenMainProps } from 'types/screens';
import { Merchandise } from 'rahtab-redux/slices/merchandisesSlice';

export const useLoadsList = (id: string): UseLoadsList => {
  const { loads } = useSelector((state) => state.loads);
  const merchandise = useSelector(inventoryMerchandiseSelector(id));
  const dispatch = useAppDispatch();
  const [idsList, setIdsList] = useState(new Set<string>());
  const [refetch, { called, loading, data, fetchMore: fetchMoreIds }] = useFetchCallType<ServerIDs['data']>(
    'GetTemplateAssignFinallList',
    {
      params: `TemplateCode=${id}&RowID=0&Count=10`,
      onCompleted: (d) => {
        let temp = new Set(idsList);
        d.forEach((loadId) => {
          temp.add(loadId);
        });
        setIdsList(temp);
      },
    },
  );

  const [_getFullInfo, fullInfo] = useFetchCallType<ServerFullInfo[]>('GetInventoryAssignFinallInfo', {
    skip: (data && data.length === 0) || !data,
    params: `AssignFinallCode=${JSON.stringify(data)}`,
    onCompleted: (d) => {
      dispatch(addLoads(d || []));
    },
  });
  const [_getAggregateInfo, aggregateInfo] = useFetchCallType<ServerAggregateInfo>('GetTemplateFinallsAggregateInfo', {
    params: `TemplateCode=${id}`,
  });

  return {
    refreshControl: {
      onRefresh: () => {
        refetch();
      },
      refreshing:
        called && fullInfo.called && (loading || (!fullInfo.called && fullInfo.loading) || aggregateInfo.loading),
    },
    screenProps: {
      data: { ...merchandise, ...aggregateInfoReducer(aggregateInfo.data)! },
      listData: Array.from(idsList.values()).map((loadId) => loads[loadId]),
      loading:
        !(called && fullInfo.called) && (loading || (!fullInfo.called && fullInfo.loading) || aggregateInfo.loading),
      transparentLoading: false,
    },
    fetchMoreIds,
  };
};

type ScreenData = Merchandise & MerchandiseInventory & AggregateInfo;
type ScreenListData = LoadFullInfo[];
export interface UseLoadsList extends ListScreenMainProps<ScreenData, ScreenListData> {
  fetchMoreIds: FetchCallTypeFn;
}

export function loadReducer(data: ServerFullInfo): LoadFullInfo {
  const {
    AssignFinallCode: assignFinallCode,
    AFIdentification: afIdentification,
    LoadName: loadName,
    RegulationTag: regulationTag,
    TemplateName: templateName,
    MiddlemanName: middlemanName,
    Amount: amount,
    MeasureCode: measureCode,
    MeasureName: measureName,
    PricePerUnit: pricePerUnit,
    TotalPrice: totalPrice,
    StatusCode: statusCode,
    Status: status,
    AssignDate: assignDate,
    DeliveryDate: deliveryDate,
    ReceiveDate: receiveDate,
    SaleAmount: saleAmount,
    RemainingValue: remainingValue,
    CorrectionAmount: correctionAmount,
    ReturnAmount: returnAmount,
    SalePercent: salePercent,
    RemainingPercent: remainingPercent,
    CorrectionPercent: correctionPercent,
    ReturnPercent: returnPercent,
    OriginalAmount: initialAmount,
    ReceivedAmount: receivedAmount,
    OriginalPricePerUnit: initialPricePerUnit,
  } = data;

  const load: LoadFullInfo = {
    assignFinallCode,
    afIdentification,
    loadName,
    regulationTag,
    templateName,
    middlemanName,
    amount,
    measureCode,
    measureName,
    pricePerUnit,
    totalPrice,
    statusCode,
    status,
    assignDate,
    deliveryDate,
    receiveDate,
    saleAmount: toNumber(saleAmount),
    remainingValue: toNumber(remainingValue),
    correctionAmount: toNumber(correctionAmount),
    returnAmount: toNumber(returnAmount),
    salePercent,
    remainingPercent,
    correctionPercent,
    returnPercent,
    initialAmount,
    receivedAmount,
    initialPricePerUnit,
  };
  return load;
}

function aggregateInfoReducer(data?: ServerAggregateInfo): AggregateInfo | undefined {
  if (!data) return;
  const { AssignFinallCount, CommentCount, MeasureName, TotalRemainingValue } = data;
  return {
    assignFinallCount: parseInt(AssignFinallCount, 10),
    totalRemainingValue: TotalRemainingValue,
    measureName: MeasureName,
    commentCount: parseFloat(CommentCount),
  };
}

interface ServerIDs {
  data: string[];
}

export interface ServerFullInfo {
  AssignFinallCode: string;
  // AssignPrimaryCode: string;
  AFIdentification: string;
  // APIdentification: string;
  LoadName: string;
  RegulationTag: string;
  TemplateName: string;
  MiddlemanName: string;
  // SupplierName: string;
  Amount: string;
  MeasureCode: string;
  MeasureName: string;
  PricePerUnit: string;
  TotalPrice: string;
  StatusCode: string;
  Status: string;
  AssignDate: string;
  DeliveryDate: string;
  ReceiveDate: string;
  SaleAmount: string;
  RemainingValue: string;
  CorrectionAmount: string;
  ReturnAmount: string;
  SalePercent: string;
  RemainingPercent: string;
  CorrectionPercent: string;
  ReturnPercent: string;
  /** مقدار اعلام وصول شده */
  ReceivedAmount: string;
  /** مقدار تخصیص واقعی */
  OriginalAmount: string;
  /** قیمت تخصیص واقعی */
  OriginalPricePerUnit: string;
}

export interface LoadFullInfo {
  assignFinallCode: string;
  afIdentification: string;
  loadName: string;
  regulationTag: string;
  templateName: string;
  middlemanName: string;
  amount: string;
  initialAmount: string;
  receivedAmount: string;
  measureCode: string;
  measureName: string;
  pricePerUnit: string;
  initialPricePerUnit: string;
  totalPrice: string;
  statusCode: string;
  status: string;
  assignDate: string;
  deliveryDate: string;
  receiveDate: string;
  saleAmount: number;
  remainingValue: number;
  correctionAmount: number;
  returnAmount: number;
  salePercent: string;
  remainingPercent: string;
  correctionPercent: string;
  returnPercent: string;
  correctionsCount?: number;
  conflictionAmount?: number;
  defectiveAmount?: number;
  outSaleAmount?: number;
  otherReasonAmount?: number;
  unitText?: string;
  conflictionPercent?: number;
  defectivePrecent?: number;
  outSalePercent?: number;
  otherReasonPercent?: number;
}

interface ServerAggregateInfo {
  AssignFinallCount: string;
  TotalRemainingValue: string;
  MeasureName: string;
  CommentCount: string;
}

interface AggregateInfo {
  assignFinallCount: number;
  totalRemainingValue: string;
  measureName: string;
  commentCount: number;
}
